import { toEquipmentAssignment } from '@sb/ai/helpers';
import {
  DEMONSTRATION_SYNTHETIC_HARDWARE_CONFIG_ID,
  DEMONSTRATION_TASK_ID_SENTINEL,
  DEMONSTRATION_USER_ID_SENTINEL,
} from '@sb/feathers-types';
import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class CreateDemonstrationRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.CreateDemonstration', 'CreateDemonstration');
  }

  protected validate({ args, ctx }: CanonicalValidateArgs): void {
    type CDArgs = Extract<
      RcStep.Arguments,
      { argumentKind: 'CreateDemonstration' }
    >;
    const a = args as CDArgs;
    this.checkSentinels(a);
    this.checkHardwareConfig(a, ctx);
  }

  // Demonstrations write to the demonstration_recordings feathers table —
  // the wizard sets the sentinel userId/taskId and a synthetic hardware
  // config id synchronously. Any other value here means the step was
  // hand-edited or saved while the wizard was loading; fail validation so
  // the routine doesn't run with a misconfigured demo.
  private checkSentinels(
    args: Extract<RcStep.Arguments, { argumentKind: 'CreateDemonstration' }>,
  ): void {
    if (args.userId !== DEMONSTRATION_USER_ID_SENTINEL) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Demonstration step user ID is misconfigured.',
        fieldId: 'userId',
      });
    }

    if (args.taskId !== DEMONSTRATION_TASK_ID_SENTINEL) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Demonstration step task ID is misconfigured.',
        fieldId: 'taskId',
      });
    }
  }

  private checkHardwareConfig(
    args: Extract<RcStep.Arguments, { argumentKind: 'CreateDemonstration' }>,
    ctx: CanonicalValidateArgs['ctx'],
  ): void {
    const { taskHardwareConfig, hardwareConfigEquipmentMapping } = args;
    const { equipment } = ctx;

    if (!taskHardwareConfig) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Demonstration mode requires bot hardware to be loaded.',
        fieldId: 'taskHardwareConfig',
      });
    }

    if (taskHardwareConfig.id !== DEMONSTRATION_SYNTHETIC_HARDWARE_CONFIG_ID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Demonstration step hardware config is misconfigured.',
        fieldId: 'taskHardwareConfig',
      });
    }

    const { config } = taskHardwareConfig;

    // The synthetic demo config defines a single robot_0. Reject any extra
    // top-level mapping entries (e.g. robot_1 carried over from a copied
    // bimanual recording step) — createRobotIdsMap reads the full mapping at
    // run time and would otherwise enable unintended secondary bots for a
    // demo that only defines robot_0.
    const allowedRobotKeys = new Set(Object.keys(config.robots));

    const extraRobotKey = Object.keys(hardwareConfigEquipmentMapping).find(
      (key) => !allowedRobotKeys.has(key),
    );

    if (extraRobotKey) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: `Unexpected equipment mapping for '${extraRobotKey}'; demonstrations only support robot_0.`,
        fieldId: 'hardwareConfigEquipmentMapping',
      });
    }

    for (const [robotKey, robot] of Object.entries(config.robots)) {
      const mapping = hardwareConfigEquipmentMapping[robotKey] ?? {
        hardwareKeyToEquipmentId: {},
      };

      for (const [hardwareKey, hardwareConfig] of Object.entries(
        robot.hardware,
      )) {
        const hardware = args.botHardware?.[hardwareConfig.id];
        const rawAssignment = mapping.hardwareKeyToEquipmentId[hardwareKey];

        const equipmentId =
          rawAssignment != null
            ? toEquipmentAssignment(rawAssignment).equipmentId
            : undefined;

        // Echo-bot's recorder treats gripper as optional in demonstration
        // mode (only pose is required), so a bot without any enabled
        // gripper can still create the step.
        if (!equipmentId && hardware?.equipmentType !== 'robot') {
          if (hardwareKey === 'gripper') continue;

          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: `Equipment not assigned for hardware key ${hardwareKey}`,
            fieldId: 'hardwareConfigEquipmentMapping',
          });
        }

        if (
          robot.isPrimary &&
          equipmentId &&
          equipment &&
          !equipment.find((e) => e.id === equipmentId)
        ) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: `Selected equipment for ${hardwareKey} is not available`,
            fieldId: 'hardwareConfigEquipmentMapping',
          });
        }
      }
    }
  }
}

export const CreateDemonstrationValidation: ValidationRule =
  new CreateDemonstrationRule().toValidationRule();
